import type { UI } from '@/v2/types';
import React, { Fragment, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Box, Container, Flex } from '@qga/roo-ui/components';

import useFlyingFrom from '@shared/v2/hooks/useFlyingFrom';

import Divider from '@/components/Divider';
import ImportantInformation from '@/components/ImportantInformation';
import useDestination from '@/hooks/useDestination';
import useTiers from '@/hooks/useTiers';
import MobileFooter from '@/v2/components/pages/PackageView/components/MobileFooter';
import OfferModals from '@/v2/components/pages/PackageView/components/OfferModals';
import Section from '@/v2/components/pages/PackageView/components/Section';
import { PACKAGE_PAGE_NAV_ITEMS } from '@/v2/components/pages/PackageView/constants';
import { useRoomOptionsData } from '@/v2/components/pages/PackageView/contexts/RoomOptionsContext';
import InPageNavigation from '@/v2/components/ui/InPageNavigation';
import usePageViewEvent from '@/v2/hooks/usePageViewEvent';
import useViewItemEvent from '@/v2/hooks/useViewItemEvent';
import DataLayer from '../../../../components/PackagePage/components/DataLayer';
import AboutHotel from './components/AboutHotel';
import CustomerValuePropositions from './components/CustomerValuePropositions';
import FlightOptionsView from './components/FlightOptionsView';
import FlyingFrom from './components/FlyingFrom';
import HotelPolicies from './components/HotelPolicies';
import ImageBanner from './components/ImageBanner';
import PackageHeader from './components/PackageHeader';
import { PackageOptionContainer } from './components/PackageOptionContainer';
import PackageOverview from './components/PackageOverview';
import RoomOptions from './components/RoomOptions';
import { useFlightModal } from './contexts/FlightModalContext';
import useIsOtherOptionsVisibleDataLayer from './hooks/useIsOtherOptionsVisisbleDataLayerEvent';

interface Props {
  propertyContent: UI.Property;
  campaign: UI.Campaign | null;
  procuredOffer: UI.ProcuredOffer | null;
}

const PackageView = ({ propertyContent, campaign, procuredOffer }: Props) => {
  useIsOtherOptionsVisibleDataLayer();

  const { asPath } = useRouter();
  const { searchConfig } = useDestination()!;
  const {
    originCode,
    setOriginCode,
    isReady: isOriginCodeReady,
  } = useFlyingFrom(searchConfig?.originCodes);
  const tiers = useTiers();
  const { packageOptions, selectedPackageOption, selectedRoomOption } =
    useRoomOptionsData() || {};

  const { isFlightModalOpen } = useFlightModal();

  const { handleInvokeViewItemEvent, isReady } = useViewItemEvent({
    property: {
      propertyContent,
      procuredOffer,
    },
    originCode,
    tiers,
  });

  useEffect(() => {
    if (isReady && isOriginCodeReady) {
      handleInvokeViewItemEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    originCode,
    selectedPackageOption,
    selectedRoomOption,
    isReady,
    isOriginCodeReady,
  ]);

  usePageViewEvent({ type: 'package' });

  return (
    <Fragment>
      <Flex flexDirection="column" bg="white">
        <Section elementKey="photos">
          <Container py={[6, null, 12]}>
            <PackageHeader propertyContent={propertyContent} />
          </Container>
          <ImageBanner propertyContent={propertyContent} />
        </Section>
        <InPageNavigation navItems={PACKAGE_PAGE_NAV_ITEMS} />
        <Section elementKey="overview">
          <PackageOverview
            propertyContent={propertyContent}
            procuredOffer={procuredOffer}
          />
          <CustomerValuePropositions />
        </Section>
        <Section elementKey="flyingFrom">
          <FlyingFrom
            value={originCode}
            onSubmit={setOriginCode}
            originCodes={searchConfig?.originCodes}
          />
        </Section>
        <Divider my="0" width="100%" />
        <Section elementKey="package">
          <PackageOptionContainer
            packageOptions={packageOptions}
            selectedPackageOption={selectedPackageOption}
            propertyContent={propertyContent}
            procuredOffer={procuredOffer}
          />
        </Section>
        <Divider my="0" width="100%" />
        <Section elementKey="room">
          <RoomOptions campaign={campaign} />
        </Section>
        <Section elementKey="about">
          <AboutHotel
            propertyContent={propertyContent}
            procuredOffer={procuredOffer}
          />
        </Section>
        <Section elementKey="policies">
          <HotelPolicies propertyContent={propertyContent} />
        </Section>
        <Box py="9">
          <Container>
            <ImportantInformation />
          </Container>
        </Box>
        {isFlightModalOpen ? (
          <>
            <DataLayer pageType="Package Page Flights View" path={asPath} />
            <FlightOptionsView
              propertyContent={propertyContent}
              procuredOffer={procuredOffer}
            />
          </>
        ) : (
          <DataLayer pageType="Package Page" path={asPath} />
        )}
      </Flex>
      <MobileFooter campaign={campaign} />
      <OfferModals />
    </Fragment>
  );
};

export default PackageView;
